<template>
    <div>
        <div class="headline">Query Details</div>
        <div class="mb-4 caption" style="color: #C0C3CF; font-size: 14px;">
            Detailed breakdown of a single NLF agent query. Review the request metadata, token consumption, estimated cost, and step-by-step agent iterations showing the messages exchanged between the system and model at each processing stage.
        </div>
        <v-simple-table dense class="mb-4">
            <tbody>
                <tr>
                    <td class="font-weight-bold">User</td>
                    <td>{{ header.user_name }}</td>
                    <td class="font-weight-bold">Created</td>
                    <td>{{ header.created_at }}</td>
                </tr>
                <tr>
                    <td class="font-weight-bold">Session ID</td>
                    <td>{{ header.session_id }}</td>
                    <td class="font-weight-bold">Model</td>
                    <td>{{ header.model_id }}</td>
                </tr>
                <tr>
                    <td class="font-weight-bold">Initial Request</td>
                    <td colspan="3">{{ requestQuery }}</td>
                </tr>
            </tbody>
        </v-simple-table>

        <h4 class="mb-2">Query Metrics</h4>
        <v-simple-table dense class="mb-4">
            <tbody>
                <tr>
                    <td class="font-weight-bold">Total Tokens</td>
                    <td style="text-align: right;">{{ ((header.total_input_tokens || 0) + (header.total_output_tokens || 0)).toLocaleString() }}</td>
                    <td class="font-weight-bold">Total Cost</td>
                    <td style="text-align: right;">${{ (Number(header.total_cost) || 0).toFixed(4) }}</td>
                </tr>
                <tr>
                    <td class="font-weight-bold">Total Duration (ms)</td>
                    <td style="text-align: right;">{{ (header.total_duration_ms || 0).toLocaleString() }}</td>
                    <td></td>
                    <td></td>
                </tr>
            </tbody>
        </v-simple-table>

        <v-card v-for="(detail, idx) in detailItems" :key="detail.id || idx" outlined class="mb-4">
            <div class="iteration-header py-2 px-3">
                <div class="font-weight-bold mb-1">Agent Iteration: {{ detail.iteration + 1 }} ({{ formatKey(detail.stop_reason) }})</div>
                <div class="d-flex align-center caption">
                    <span class="mr-4"><span class="font-weight-bold">Input Tokens:</span> {{ (detail.input_tokens || 0).toLocaleString() }}</span>
                    <span class="mr-4"><span class="font-weight-bold">Output Tokens:</span> {{ (detail.output_tokens || 0).toLocaleString() }}</span>
                    <span class="mr-4"><span class="font-weight-bold">Cost:</span> ${{ (Number(detail.token_cost) || 0).toFixed(4) }}</span>
                    <span><span class="font-weight-bold">Duration:</span> {{ (detail.duration_ms || 0).toLocaleString() }}ms</span>
                </div>
            </div>
            <v-divider />
            <v-card-text>
                <h4 class="mb-1">Input</h4>
                <v-simple-table dense class="mb-3">
                    <thead>
                        <tr>
                            <th style="width: 120px;">Type</th>
                            <th style="width: 80px;">Role</th>
                            <th>Content</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, itemIdx) in detail.input" :key="'input-' + itemIdx">
                            <td class="font-weight-bold" style="vertical-align: top;">{{ formatKey(item.type) }}</td>
                            <td style="vertical-align: top;">{{ item.role }}</td>
                            <td
                                :class="['content-cell', { zoomable: isCellZoomable(item, 'system_prompt') }]"
                                :tabindex="isCellZoomable(item, 'system_prompt') ? 0 : undefined"
                                :role="isCellZoomable(item, 'system_prompt') ? 'button' : undefined"
                                :aria-label="isCellZoomable(item, 'system_prompt') ? 'View full content' : undefined"
                                @click="isCellZoomable(item, 'system_prompt') && showZoom(item.content)"
                                @keydown.enter="isCellZoomable(item, 'system_prompt') && showZoom(item.content)"
                                @keydown.space.prevent="isCellZoomable(item, 'system_prompt') && showZoom(item.content)"
                            >
                                {{ truncate(item.content) }}
                            </td>
                        </tr>
                    </tbody>
                </v-simple-table>
                <h4 class="mb-1">Output</h4>
                <v-simple-table dense>
                    <thead>
                        <tr>
                            <th style="width: 120px;">Type</th>
                            <th style="width: 80px;">Role</th>
                            <th>Content</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, itemIdx) in detail.output" :key="'output-' + itemIdx">
                            <td class="font-weight-bold" style="vertical-align: top;">{{ formatKey(item.type) }}</td>
                            <td style="vertical-align: top;">{{ item.role }}</td>
                            <td
                                :class="['content-cell', { zoomable: isCellZoomable(item, 'reasoning') }]"
                                :tabindex="isCellZoomable(item, 'reasoning') ? 0 : undefined"
                                :role="isCellZoomable(item, 'reasoning') ? 'button' : undefined"
                                :aria-label="isCellZoomable(item, 'reasoning') ? 'View full content' : undefined"
                                @click="isCellZoomable(item, 'reasoning') && showZoom(item.content)"
                                @keydown.enter="isCellZoomable(item, 'reasoning') && showZoom(item.content)"
                                @keydown.space.prevent="isCellZoomable(item, 'reasoning') && showZoom(item.content)"
                            >
                                {{ truncate(item.content) }}
                            </td>
                        </tr>
                    </tbody>
                </v-simple-table>
                <template v-if="detail.showFeedback">
                    <h4 class="mb-1 mt-3">Feedback</h4>
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <td class="font-weight-bold" style="width: 120px; vertical-align: top;">Rating</td>
                                <td data-test="nlf-logs-detail-feedback-rating">{{ detail.queryRating || 'none' }}</td>
                            </tr>
                            <tr>
                                <td class="font-weight-bold" style="width: 120px; vertical-align: top;">Comments</td>
                                <td data-test="nlf-logs-detail-feedback-comments">{{ detail.queryFeedbackText || 'none' }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </template>
            </v-card-text>
        </v-card>

        <div class="d-flex justify-center mt-4 mb-2">
            <v-btn text small data-test="nlf-logs-back-to-top-btn" @click="scrollToTop">
                <v-icon small class="mr-1">fas fa-arrow-up</v-icon>
                Back to top
            </v-btn>
        </div>

        <v-dialog v-model="zoomDialog" max-width="700" scrollable>
            <v-card>
                <v-card-title class="d-flex justify-space-between align-center">
                    <span>Content</span>
                    <fe-icon-btn
                        useIcon="fas fa-times"
                        usage="ghost"
                        aria-label="Close dialog"
                        data-test="nlf-logs-zoom-close-btn"
                        @click="zoomDialog = false"
                    />
                </v-card-title>
                <v-card-text>
                    <pre class="zoom-pre">{{ zoomContent }}</pre>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
    import nlfLogsMixin from './nlfLogsMixin'

    export default {
        name: 'NlfLogsQueryDetail',
        mixins: [nlfLogsMixin],
        props: {
            header: {
                type: Object,
                required: true
            },
            detailItems: {
                type: Array,
                required: true
            }
        },
        data() {
            return {
                zoomDialog: false,
                zoomContent: ''
            }
        },
        computed: {
            requestQuery() {
                return this.header?.request?.query
            }
        },
        methods: {
            isCellZoomable(item, alwaysZoomType) {
                return item.type === alwaysZoomType || this.isZoomable(item.content)
            },
            isZoomable(content) {
                if (!content) return false
                let str = typeof content === 'string' ? content : JSON.stringify(content)
                if (str.length > 250) return true
                if (str.startsWith('```json')) return true
                try {
                    JSON.parse(str)
                    return true
                } catch {
                    return false
                }
            },
            showZoom(content) {
                let str = typeof content === 'string' ? content : JSON.stringify(content, null, 2)
                try {
                    this.zoomContent = JSON.stringify(JSON.parse(str), null, 2)
                } catch {
                    this.zoomContent = str
                }
                this.zoomDialog = true
            }
        }
    }
</script>

<style lang="scss" scoped>
.iteration-header {
    background: #f5f5f5;
    border-left: 4px solid #1976d2;
}

.content-cell.zoomable {
    cursor: zoom-in;
}

.zoom-pre {
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 12px;
    font-family: monospace;
}
</style>
